import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaEllipsisV, FaSpinner, FaUpload } from 'react-icons/fa';
import '../styles/Services.css';

const BASE_URL = 'https://musa.nawetechltd.co.ke';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const [form, setForm] = useState({
    name: '', description: '', image_url: '', button_text: '', category_id: ''
  });

  const token = localStorage.getItem('adminToken');

  const fetchData = async () => {
    try {
      const [sRes, cRes] = await Promise.all([
        fetch('/api/services', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/services/categories', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (sRes.ok) setServices(await sRes.json());
      if (cRes.ok) setCategories(await cRes.json());
    } catch (err) { console.error('Failed to fetch:', err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const handleClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      setForm({ ...form, image_url: `/images/services/${Date.now()}-${file.name}` });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      let finalImageUrl = form.image_url;
      if (imageFile) {
        const formData = new FormData(); formData.append('image', imageFile);
        const uploadRes = await fetch('/api/upload', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
        if (uploadRes.ok) { const uploadData = await uploadRes.json(); finalImageUrl = BASE_URL + uploadData.url; }
      } else if (form.image_url && !form.image_url.startsWith('http')) { finalImageUrl = BASE_URL + form.image_url; }
      const url = editingId ? `/api/services/${editingId}` : '/api/services';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ ...form, image_url: finalImageUrl, category_id: parseInt(form.category_id) || null }) });
      if (res.ok) { setShowForm(false); setEditingId(null); setImageFile(null); setImagePreview(''); setForm({ name: '', description: '', image_url: '', button_text: '', category_id: '' }); fetchData(); }
    } catch (err) { console.error('Save error:', err); }
    setSaving(false);
  };

  const handleEdit = (service) => {
    setForm({ name: service.name || '', description: service.description || '', image_url: service.image_url || '', button_text: service.button_text || '', category_id: service.category_id || '' });
    setImagePreview(service.image_url || ''); setImageFile(null);
    setEditingId(service.id); setShowForm(true); setOpenMenuId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await fetch(`/api/services/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    setOpenMenuId(null); fetchData();
  };

  const handleMenuClick = (e, serviceId) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    // Position dropdown ABOVE the button, aligned to the left
    setMenuPosition({ 
      top: rect.top - 10,  // Above the button
      left: rect.left - 140 // To the left of the button
    });
    setOpenMenuId(openMenuId === serviceId ? null : serviceId);
  };

  if (loading) return <div className="admin-page"><p><FaSpinner className="spinner" /> Loading...</p></div>;

  return (
    <div className="services-page">
      <div className="page-header">
        <div><h1>Our Services</h1><p>Manage your electrical services. {services.length} total</p></div>
        <button className="add-btn" onClick={() => { setEditingId(null); setImageFile(null); setImagePreview(''); setForm({ name: '', description: '', image_url: '', button_text: '', category_id: '' }); setShowForm(true); }}><FaPlus /> Add New Service</button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{editingId ? 'Edit Service' : 'Add New Service'}</h2><button className="close-btn" onClick={() => setShowForm(false)}><FaTimes /></button></div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Service Name *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Internet & Network Installation" /></div>
              <div className="form-group"><label>Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="3" placeholder="Service description..." /></div>
              <div className="form-group"><label>Image Upload</label>
                <div className="image-upload-area">
                  <input type="file" accept="image/*" onChange={handleImageChange} id="service-image" style={{ display: 'none' }} />
                  <label htmlFor="service-image" className="upload-label">{imagePreview ? <img src={imagePreview} alt="Preview" className="upload-preview" /> : <div className="upload-placeholder"><FaUpload /> Click to upload image</div>}</label>
                </div>
                <input type="text" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://musa.nawetechltd.co.ke/images/services/..." className="url-input" />
              </div>
              <div className="form-row">
                <div className="form-group"><label>Button Text</label><input type="text" value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} placeholder="e.g. Get Connected" /></div>
                <div className="form-group"><label>Category</label><select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}><option value="">Select category</option>{categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}</select></div>
              </div>
              <div className="form-actions"><button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>Cancel</button><button type="submit" className="submit-btn" disabled={saving}><FaSave /> {saving ? 'Saving...' : 'Save Service'}</button></div>
            </form>
          </div>
        </div>
      )}

      <div className="table-wrapper" style={{ position: 'relative' }}>
        <table className="services-table">
          <thead><tr><th>#</th><th>Service</th><th>Image</th><th>Button</th><th>Category</th><th>Actions</th></tr></thead>
          <tbody>
            {services.length === 0 ? <tr><td colSpan="6" className="empty-msg">No services found.</td></tr> :
              services.map((service, i) => (
                <tr key={service.id}>
                  <td>{i + 1}</td>
                  <td><strong>{service.name}</strong></td>
                  <td>{service.image_url ? <img src={service.image_url} alt="" className="thumb-img" onError={(e) => e.target.style.display = 'none'} /> : 'No image'}</td>
                  <td>{service.button_text || 'N/A'}</td>
                  <td><span className="cat-badge">{service.category_name || 'N/A'}</span></td>
                  <td className="actions-cell">
                    <button className="menu-btn" onClick={(e) => handleMenuClick(e, service.id)}><FaEllipsisV /></button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>

        {openMenuId && (
          <div className="dropdown-menu" style={{ 
            position: 'fixed', 
            top: menuPosition.top, 
            left: menuPosition.left, 
            zIndex: 9999,
            transform: 'translateY(-100%)'
          }}>
            <button onClick={() => { const s = services.find(s => s.id === openMenuId); if (s) handleEdit(s); }}><FaEdit /> Edit</button>
            <button onClick={() => handleDelete(openMenuId)} className="delete-item"><FaTrash /> Delete</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminServices;
